import { useState } from 'react';
import { useDraftSlot, usePendingMarker } from '@/shared/hooks/draftSlots';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  contractorNested,
  type ContractorContact, type ContractRow, type ContractorBankRow,
  type ContractorDeliveryAddress,
} from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import TchTable from '@/shared/components/TchTable';



interface OrgOption { id: string; name: string; is_vat_payer: boolean; use_returnable_packaging: boolean }
interface CurrencyOption { id: string; code: string }
interface CivilKindOption { id: string; name: string }
interface ProjectOption { id: string; name: string }

// ============================ Договори ============================

export function ContractsTab(props: {
  contractorId: string;
  contracts: ContractRow[];
  orgs: OrgOption[];
  currencies: CurrencyOption[];
  civilKinds: CivilKindOption[];
  projects: ProjectOption[];
  onChanged: () => void;
}) {
  // 2026-07-23 (рішення власника): вкладка — лише СПИСОК (Осн./Номер/Назва/
  // Організація/Дата/Валюта). Назва — посилання на сторінку договору
  // (/refs/contracts/:id); створення — окрема сторінка з предзаповненим
  // контрагентом. Повна картка з 4 вкладками 1С живе там.
  const { contractorId, contracts, orgs, currencies, onChanged } = props;
  const navigate = useNavigate();
  const confirm = useConfirm();

  const currCode = (cid: string) => currencies.find((c) => c.id === cid)?.code ?? '';
  const orgName = (oid: string) => orgs.find((o) => o.id === oid)?.name ?? '—';

  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeContract(id),
    onSuccess: onChanged,
  });
  // 0373: основний договір — один на (org, contractor).
  const setMain = useMutation({
    mutationFn: async (target: ContractRow) => {
      const cur = contracts.find(
        (c) => c.is_main && c.organization_id === target.organization_id && c.id !== target.id,
      );
      if (cur) await contractorNested.updateContract(cur.id, { is_main: false });
      return contractorNested.updateContract(target.id, { is_main: !target.is_main });
    },
    onSuccess: onChanged,
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="tabs__panel">
      {contracts.length === 0 && <p style={{ color: 'var(--muted)' }}>Договорів немає.</p>}
      {contracts.length > 0 && (
        <TchTable>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ textAlign: 'center' }}
                title="Основний договір — підставляється у документи">Осн.</th>
              <th>Номер</th>
              <th>Назва</th>
              <th>Організація</th>
              <th>Дата</th>
              <th>Валюта</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                <td style={{ textAlign: 'center' }}>
                  {/* ч.747: радіо замість зірочки — один основний договір на організацію. */}
                  <input type="radio" name={`contract-main-${c.organization_id}`}
                    checked={c.is_main}
                    disabled={setMain.isPending}
                    title={c.is_main ? 'Основний договір' : 'Зробити основним'}
                    onChange={() => { if (!c.is_main) setMain.mutate(c); }}
                    style={{ width: 'auto', margin: 0, verticalAlign: 'middle', cursor: 'pointer' }} />
                </td>
                <td>{c.number || c.code}</td>
                <td>
                  <a href={`/refs/contracts/${c.id}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/refs/contracts/${c.id}`); }}
                    style={{ color: '#06c', textDecoration: 'none' }}>
                    {c.name}
                  </a>
                  {c.valid_until && c.valid_until < today && (
                    <span title={`Строк дії минув ${c.valid_until}`}
                      style={{ marginLeft: 6, fontSize: '0.72rem', color: '#6b7280',
                               border: '1px solid #d1d5db', borderRadius: 4, padding: '0 4px' }}>
                      строк минув
                    </span>
                  )}
                </td>
                <td>{orgName(c.organization_id)}</td>
                <td>{c.contract_date ?? '—'}</td>
                <td>{currCode(c.currency_id) || '—'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="danger" title="Видалити"
                    onClick={() => confirm(`Видалити договір «${c.name}»?`).then((ok) => { if (ok) remove.mutate(c.id); })}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}

      <button type="button" style={{ marginTop: '0.5rem' }}
        onClick={() => navigate(`/refs/contracts/new?contractor_id=${contractorId}`)}>
        + Додати договір
      </button>
    </div>
  );
}


// ============================ Контактні особи ============================

export function ContactsTab(props: {
  contractorId: string;
  contacts: ContractorContact[];
  onChanged: () => void;
}) {
  const { contractorId, contacts, onChanged } = props;
  const confirm = useConfirm();
  // ч.1060: рядок «новий контакт» живе у картці (слоти) — не губиться при
  // перемиканні вкладок і потрапляє у чернетку картки.
  const [showAdd, setShowAdd] = useDraftSlot('contacts.showAdd', false);
  const [name, setName] = useDraftSlot('contacts.name', '');
  const [position, setPosition] = useDraftSlot('contacts.position', '');
  const [phone, setPhone] = useDraftSlot('contacts.phone', '');
  const [email, setEmail] = useDraftSlot('contacts.email', '');
  const [birthday, setBirthday] = useDraftSlot('contacts.birthday', '');
  usePendingMarker('contacts', showAdd && [name, position, phone, email, birthday]
    .some((v) => v.trim() !== '') ? 'новий контакт' : null);

  const create = useMutation({
    mutationFn: () => contractorNested.createContact({
      contractor_id: contractorId, name: name.trim(),
      position: position.trim() || null, phone: phone.trim() || null,
      email: email.trim() || null, birthday: birthday || null,
      is_main: contacts.length === 0,
    }),
    onSuccess: () => { setShowAdd(false); setName(''); setPosition(''); setPhone(''); setEmail(''); setBirthday(''); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeContact(id),
    onSuccess: onChanged,
  });

  return (
    <div className="tabs__panel">
      {contacts.length === 0 && <p style={{ color: 'var(--muted)' }}>Контактних осіб немає.</p>}
      {contacts.length > 0 && (
        <TchTable>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th>ПIБ</th>
              <th>Посада</th>
              <th>Телефон</th>
              <th>Email</th>
              <th>День нар.</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                <td>{c.name} {c.is_main && <span style={{ color: 'var(--primary, #06c)' }}>(осн.)</span>}</td>
                <td>{c.position ?? '—'}</td>
                <td>{c.phone ?? '—'}</td>
                <td>{c.email ?? '—'}</td>
                <td>{c.birthday ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="danger" title="Видалити"
                    onClick={() => confirm(`Видалити контакт «${c.name}»?`).then((ok) => { if (ok) remove.mutate(c.id); })}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}
      {!showAdd ? (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => setShowAdd(true)}>+ Додати контакт</button>
      ) : (
        <fieldset style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ccc' }}>
          <legend>Новий контакт</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <label>ПIБ <span style={{ color: 'red' }}>*</span>
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Посада
              <input value={position} onChange={(e) => setPosition(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Телефон
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>День народження
              <DatePopover value={birthday} onChange={(v) => setBirthday(v)} style={{ width: '100%' }} />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Зберегти</button>
            <button onClick={() => {
              setShowAdd(false); setName(''); setPosition(''); setPhone(''); setEmail(''); setBirthday('');
            }}>Скасувати</button>
          </div>
        </fieldset>
      )}
    </div>
  );
}

// ==================== Адреси постачання (0385) ====================

export function DeliveryAddressesTab(props: {
  contractorId: string;
  addresses: ContractorDeliveryAddress[];
  onChanged: () => void;
}) {
  const { contractorId, addresses, onChanged } = props;
  const confirm = useConfirm();
  // ч.1060: рядок «нова адреса» — у слотах картки (див. ContactsTab).
  const [showAdd, setShowAdd] = useDraftSlot('delivery.showAdd', false);
  const [label, setLabel] = useDraftSlot('delivery.label', '');
  const [address, setAddress] = useDraftSlot('delivery.address', '');
  usePendingMarker('delivery', showAdd && (label.trim() !== '' || address.trim() !== '')
    ? 'нова адреса постачання' : null);

  const create = useMutation({
    mutationFn: () => contractorNested.createDeliveryAddress({
      contractor_id: contractorId,
      name: label.trim() || null,
      address: address.trim(),
      is_main: addresses.length === 0,
    }),
    onSuccess: () => { setShowAdd(false); setLabel(''); setAddress(''); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeDeliveryAddress(id),
    onSuccess: onChanged,
  });

  return (
    <div className="tabs__panel">
      {addresses.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>
          Адрес постачання немає. Вони підставляються у ТТН як «Адреса призначення».
        </p>
      )}
      {addresses.length > 0 && (
        <TchTable>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ width: 220 }}>Назва</th>
              <th>Адреса</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {addresses.map((a) => (
              <tr key={a.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                <td>
                  {a.name ?? '—'} {a.is_main && <span style={{ color: 'var(--primary, #06c)' }}>(осн.)</span>}
                </td>
                <td>{a.address}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="danger" title="Видалити"
                    onClick={() => confirm('Видалити адресу постачання?').then((ok) => { if (ok) remove.mutate(a.id); })}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      )}
      {!showAdd ? (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => setShowAdd(true)}>+ Додати адресу</button>
      ) : (
        <fieldset style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ccc' }}>
          <legend>Нова адреса постачання</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
            <label>Назва (необов'язково)
              <input value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="напр. Склад Київ" style={{ width: '100%' }} />
            </label>
            <label>Адреса <span style={{ color: 'red' }}>*</span>
              <input value={address} onChange={(e) => setAddress(e.target.value)}
                style={{ width: '100%' }} maxLength={500} />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" disabled={address.trim().length < 3 || create.isPending}
              onClick={() => create.mutate()}>Зберегти</button>
            <button onClick={() => { setShowAdd(false); setLabel(''); setAddress(''); }}>Скасувати</button>
          </div>
        </fieldset>
      )}
    </div>
  );
}

// ============================ Банк-рахунки ============================

export interface BankAccountDraft {
  iban: string;
  currency_id: string;
  account_name: string | null;
  is_main: boolean;
}

/**
 * ч.742: вкладка «банкрахунки» картки контрагента — КОНТРОЛЬОВАНА (список +
 * колбеки). Для збереженого контрагента батько ходить в API, для нового —
 * тримає рахунки локально і створює їх після збереження картки. Назва банку
 * НЕ вводиться — тягнеться з довідника банків за МФО IBAN (символи 5–10).
 * Основний рахунок (is_main) — саме його бере експорт реєстру платіжок у банк.
 */
export function BankTab(props: {
  accounts: ContractorBankRow[];
  currencies: CurrencyOption[];
  bankNameByIban: (iban: string) => string | null;
  onAdd: (d: BankAccountDraft) => Promise<void> | void;
  onRemove: (id: string) => Promise<void> | void;
  onSetMain: (id: string) => Promise<void> | void;
  pending?: boolean;
}) {
  const { accounts, currencies, bankNameByIban, onAdd, onRemove, onSetMain, pending } = props;
  const confirm = useConfirm();
  // ч.1060: рядок «новий рахунок» — у слотах картки: IBAN, набраний до
  // «додати», не губиться при перемиканні вкладок і потрапляє у чернетку.
  const [iban, setIban] = useDraftSlot('bankTab.iban', '');
  const [currencyId, setCurrencyId] = useDraftSlot('bankTab.currencyId', '');
  const [accountName, setAccountName] = useDraftSlot('bankTab.accountName', '');
  const [busy, setBusy] = useState(false);
  usePendingMarker('bankTab', iban.trim() !== '' || accountName.trim() !== ''
    ? 'новий банківський рахунок' : null);

  const uahId = currencies.find((c) => c.code === 'UAH')?.id ?? '';
  const cleanIban = iban.replace(/\s+/g, '').toUpperCase();
  const ibanOk = cleanIban.length >= 15;
  const liveBank = bankNameByIban(cleanIban);
  const currCode = (cid: string) => currencies.find((c) => c.id === cid)?.code ?? '';

  const add = async () => {
    setBusy(true);
    try {
      await onAdd({
        iban: cleanIban, currency_id: currencyId || uahId,
        account_name: accountName.trim() || null,
        is_main: accounts.length === 0,
      });
      setIban(''); setAccountName(''); setCurrencyId('');
    } finally { setBusy(false); }
  };

  return (
    <div className="tabs__panel">
      {pending && (
        <p style={{ color: 'var(--muted)', fontSize: '0.85em', marginTop: 0 }}>
          Рахунки буде створено разом зі збереженням картки.
        </p>
      )}
      {accounts.length === 0 && <p style={{ color: 'var(--muted)' }}>Банк-рахунків немає.</p>}
      {accounts.length > 0 && (
        <TchTable>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th>IBAN</th>
              <th>Банк</th>
              <th style={{ width: 70 }}>Валюта</th>
              <th>Назва рахунку</th>
              <th style={{ width: 150 }}>Основний</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((ba) => {
              const bank = bankNameByIban(ba.iban);
              return (
                <tr key={ba.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{ba.iban}</td>
                  <td title="Назва банку — з довідника за МФО IBAN">
                    {bank ?? <span style={{ color: 'var(--muted)' }}>— (МФО не знайдено)</span>}
                  </td>
                  <td>{currCode(ba.currency_id) || 'UAH'}</td>
                  <td>{ba.account_name ?? '—'}</td>
                  <td>
                    {ba.is_main ? (
                      <span style={{ color: 'green', fontWeight: 600 }}>✓ основний</span>
                    ) : (
                      <button type="button" disabled={busy}
                        title="Саме основний рахунок підставляється у платіжки та реєстр у банк"
                        onClick={async () => { setBusy(true); try { await onSetMain(ba.id); } finally { setBusy(false); } }}>
                        зробити основним
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="danger" title="Видалити" disabled={busy}
                      onClick={() => confirm('Видалити банк-рахунок?').then(async (ok) => {
                        if (!ok) return;
                        setBusy(true);
                        try { await onRemove(ba.id); } finally { setBusy(false); }
                      })}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr auto', gap: '0.5rem',
                    alignItems: 'end', marginTop: '0.75rem', maxWidth: 900 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>IBAN
            {liveBank && (
              <small style={{ color: 'green', marginLeft: 6 }}>{liveBank}</small>
            )}
            {!liveBank && cleanIban.length >= 10 && (
              <small style={{ color: 'var(--muted)', marginLeft: 6 }}>банк за МФО не знайдено</small>
            )}
          </label>
          <input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="UA…"
            style={{ fontFamily: 'monospace' }} maxLength={34} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Валюта</label>
          <select value={currencyId || uahId} onChange={(e) => setCurrencyId(e.target.value)}>
            {currencies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Назва рахунку <small style={{ color: 'var(--muted)' }}>(необовʼязково)</small></label>
          <input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
        </div>
        <button type="button" className="primary" disabled={!ibanOk || busy} onClick={() => void add()}>
          додати рахунок
        </button>
      </div>
    </div>
  );
}
